import type { LLMProvider } from "@pick-three-ai/constants";
import { LLM_PROVIDERS } from "@pick-three-ai/constants";

// Import only the specific icons we need (tree-shakeable)
import {
  siOpenai,
  siAnthropic,
  siGooglegemini,
  siMeta,
  siCursor,
  siMistralai,
} from "simple-icons";
import type { SimpleIcon } from "simple-icons";

// Import custom SVG icons (not available in simple-icons)
import grokSvgRaw from "../assets/icons/grok.svg?raw";
import deepseekSvgRaw from "../assets/icons/deepseek.svg?raw";
import qwenSvgRaw from "../assets/icons/qwen.svg?raw";
import cohereSvgRaw from "../assets/icons/cohere.svg?raw";

interface ProviderIconProps {
  provider: LLMProvider;
  size?: number;
  className?: string;
}

// Custom icon type for SVGs not in simple-icons
interface CustomIcon {
  path?: string;
  viewBox: string;
  fullSvg?: string; // For multi-path SVGs with gradients, etc.
}

// Parse SVG string to extract path and viewBox
function parseSvgIcon(svgString: string): CustomIcon | null {
  const viewBoxMatch = svgString.match(/viewBox="([^"]+)"/);
  const pathMatch = svgString.match(/<path[^>]*d="([^"]+)"/);

  if (viewBoxMatch?.[1]) {
    // Check if this is a multi-path SVG (has gradients, multiple paths, etc.)
    const hasGradients = svgString.includes("<defs>") || svgString.includes("gradient");
    const pathMatches = svgString.match(/<path/g);
    const isMultiPath = pathMatches && pathMatches.length > 1;

    if (hasGradients || isMultiPath) {
      // Return full SVG for complex icons
      return {
        viewBox: viewBoxMatch[1],
        fullSvg: svgString,
      };
    }

    // Single path icon
    if (pathMatch?.[1]) {
      return {
        viewBox: viewBoxMatch[1],
        path: pathMatch[1],
      };
    }
  }
  return null;
}

// Map our provider keys to simple-icons - only include icons that exist in simple-icons
const PROVIDER_ICONS: Partial<Record<LLMProvider, SimpleIcon>> = {
  openai: siOpenai,
  anthropic: siAnthropic,
  google: siGooglegemini, // Use Gemini logo for Google models
  meta: siMeta,
  cursor: siCursor,
  mistral: siMistralai,
};

// Custom icons for providers not in simple-icons (imported from assets/icons/)
const CUSTOM_ICONS: Partial<Record<LLMProvider, CustomIcon | null>> = {
  xai: parseSvgIcon(grokSvgRaw),
  deepseek: parseSvgIcon(deepseekSvgRaw),
  alibaba: parseSvgIcon(qwenSvgRaw),
  cohere: parseSvgIcon(cohereSvgRaw),
};

export function ProviderIcon({
  provider,
  size = 24,
  className = "",
}: ProviderIconProps) {
  const iconData = PROVIDER_ICONS[provider];
  const customIcon = CUSTOM_ICONS[provider];
  const providerInfo = LLM_PROVIDERS[provider];

  // Check for custom icon first
  if (customIcon) {
    // Handle full SVG content (for multi-path icons with gradients)
    if (customIcon.fullSvg) {
      // Extract the inner content (everything between <svg> tags)
      const innerContentMatch = customIcon.fullSvg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
      const innerContent = innerContentMatch?.[1] || "";
      
      // Check if paths use currentColor (like qwen after conversion, cohere)
      const pathsUseCurrentColor = innerContent.includes('fill="currentColor"') || 
                                    innerContent.includes("fill='currentColor'");
      
      // Check if paths have explicit fills (gradients, colors other than currentColor)
      const pathsHaveExplicitFills = (innerContent.includes('fill="') || 
                                       innerContent.includes("fill='") ||
                                       innerContent.includes('url(#')) && !pathsUseCurrentColor;
      
      // Use currentColor if paths use it or if there are no explicit fills
      const fillProp = pathsUseCurrentColor || !pathsHaveExplicitFills ? "currentColor" : undefined;

      return (
        <div
          className={`flex items-center justify-center ${className}`}
          style={{ width: size, height: size }}
        >
          <svg
            role="img"
            viewBox={customIcon.viewBox}
            width={size}
            height={size}
            fill={fillProp}
            aria-label={providerInfo.name}
            xmlns="http://www.w3.org/2000/svg"
            dangerouslySetInnerHTML={{ __html: innerContent }}
          />
        </div>
      );
    }

    // Handle single-path icon
    if (customIcon.path) {
      return (
        <div
          className={`flex items-center justify-center ${className}`}
          style={{ width: size, height: size }}
        >
          <svg
            role="img"
            viewBox={customIcon.viewBox}
            width={size}
            height={size}
            fill="currentColor"
            aria-label={providerInfo.name}
          >
            <path d={customIcon.path} />
          </svg>
        </div>
      );
    }
  }

  if (!iconData) {
    // Fallback to first letter if icon not found
    return (
      <div
        className={`flex items-center justify-center rounded-lg font-semibold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: providerInfo.color,
          color: "#fff",
          fontSize: size * 0.5,
        }}
      >
        {providerInfo.name.charAt(0)}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        role="img"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        aria-label={providerInfo.name}
      >
        <path d={iconData.path} />
      </svg>
    </div>
  );
}

